import { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Modal as BootstrapModal } from "react-bootstrap";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import TextBox from "../controls/form/TextBox";
import Domain from "../../services/domain";
import { useTranslation } from "react-i18next";
import { useGetUser } from "../../common/Hooks";
import User from "../../services/user";
import PubSub from "../../services/PubSub";
import Toggle from "../controls/Toggle";
import Utils from "../../utils/utils";

const nameRegex = /^[a-zA-Z0-9-\s]+[a-zA-Z0-9]$/;

const CreateDomain = ({ show, onCancel }) => {
    const [translate] = useTranslation();
    const [productList, setProductList] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [, setUser] = useGetUser();
    const [requiresLicense, setRequiresLicense] = useState(false);

    const getProducts = useCallback(async () => {
        const response = await Domain.getAllProductsByDomain(Utils.getDomain());
        if (!response.error) {
            setProductList(response.data.map((product) => {
                return {
                    _id: product._id,
                    name: product.name
                }
            }));
        }
    }, [setProductList]);

    useEffect(() => {
        if (show) {
            getProducts();
        }
    }, [show, getProducts]);

    const create = async ({ name, description }) => {
        const response = await Domain.createDomain({
            name,
            description,
            requiresLicense,
            products: selectedProducts
        });
        if (!response.error) {
            const updatedUser = await User.getUser();
            if (!updatedUser.error) {
                setUser({
                    _id: updatedUser.data._id,
                    email: updatedUser.data.email,
                    appAdmin: updatedUser.data.appAdmin,
                    firstName: updatedUser.firstName,
                    lastName: updatedUser.lastName,
                    adminForDomains: updatedUser.data.adminForDomains,
                    uiPermissions: updatedUser.data.uiPermissions
                });
            }
            PubSub.publish("refresh-domain-list", { newDomainId: response.data._id });
            onCancel();
        }
    };

    const updateSelectedProducts = useCallback(({ _id }) => {
        setSelectedProducts(products => {
            if (products.indexOf(_id) > -1) {
                products.splice(products.indexOf(_id), 1);
            } else {
                products.push(_id);
            }
            return products;
        });
    }, [setSelectedProducts]);

    const renderProductList = useMemo(() => (
        <div className="mt-3">
            <label htmlFor="products" className="form-label h6 d-block">{translate("OTM_PRODUCTS")}</label>
            {productList.length ? 
                <div className="row mt-n2">
                    {productList.map(product => (
                        <div className="col-12 col-lg-6 mt-2" key={product._id}>
                            <div className="form-check form-switch" id="products">
                                <Toggle
                                    id={product._id}
                                    onChange={() => updateSelectedProducts(product)}
                                />
                                <label className="form-check-label ms-2" htmlFor={product._id}>
                                    {product.name}
                                </label>
                            </div>
                        </div>
                    ))}
                </div> : 
                <label>{translate("OTM_NO_PRODUCTS")}</label> }
        </div>
    ), [productList, updateSelectedProducts, translate]);

    const renderLicenseRequired = useMemo(() => (
        <div className="mt-3">
            <label htmlFor="domain" className="form-label h6">{translate("OTM_REQUIRES_LICENSE")}</label>
            <div className="form-check form-switch">
                <Toggle
                    defaultState={false}
                    onChange={() => setRequiresLicense((currentValue) => !currentValue)}
                />
            </div>
        </div>
    ), [translate]);

    return (
        <BootstrapModal show={show} backdrop="static" size="lg">
            <Formik initialValues={{
                name: "",
                description: "",
            }} validationSchema={Yup.object({
                name: Yup.string().matches(nameRegex, translate("OTM_INVALID_NAME")).min(3, translate("OTM_CHARACTERS_OR_MORE", { minCharacters: 3 })).required(" "),
            })} onSubmit={create}>
                <Form>
                    <BootstrapModal.Header className="h5 text-secondary">{translate("OTM_CREATE_DOMAIN")}</BootstrapModal.Header>
                    <BootstrapModal.Body>
                        <TextBox label={translate("OTM_NAME")} type="text" name="name" />
                        <TextBox label={translate("OTM_DESCRIPTION")} type="text" name="description" css="mt-3" />
                        {renderLicenseRequired}
                        {renderProductList}
                    </BootstrapModal.Body>
                    <BootstrapModal.Footer>
                        <Button type="submit" className="btn-sm">{translate("OTM_CREATE")}</Button>
                        <Button onClick={onCancel} variant="secondary" className="btn-sm">{translate("OTM_CANCEL")}</Button>
                    </BootstrapModal.Footer>
                </Form>
            </Formik>
        </BootstrapModal>
    );
};

export default CreateDomain;
